
import { createClient } from "@supabase/supabase-js";
import type { Database } from './types';
import { IS_NATIVE } from "@/mobile/appMode";

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  console.error("Missing Supabase environment variables");
}

/**
 * Global flag to track if we're in password recovery mode.
 * IMPORTANT: This is detected SYNCHRONOUSLY from the URL hash BEFORE
 * the Supabase client is created, ensuring we catch it before any
 * auth processing happens.
 */
function detectRecoveryFromUrl(): boolean {
  // Check URL hash for type=recovery (Supabase puts auth params in hash)
  const hash = window.location.hash.substring(1);
  const params = new URLSearchParams(hash);
  return params.get('type') === 'recovery';
}

export const isPasswordRecoveryMode = detectRecoveryFromUrl();

/**
 * Marketing "live embed" context (P6-C). The read-only demo embed renders the
 * real app inside an iframe on the SAME origin as noknok.pro. If its Supabase
 * client wrote to the normal auth-storage keys it would clobber a logged-in
 * user's session the moment they opened a marketing page with a live tile.
 * So when we detect the embed we give its client an ISOLATED, in-memory store
 * under a distinct storageKey — the viewer session lives only for the life of
 * the iframe document and never persists to localStorage / cookie / IDB.
 * Detected synchronously from the URL, before the client is created.
 */
export const IS_DEMO_EMBED = (() => {
  try {
    const u = new URL(window.location.href);
    return u.pathname.startsWith('/demo-embed') || u.searchParams.get('demoEmbed') === '1';
  } catch {
    return false;
  }
})();

// If we're in recovery mode, redirect to reset password page immediately
// This happens BEFORE React even mounts
if (isPasswordRecoveryMode && window.location.pathname !== '/reset-password') {
  // Preserve the hash params for Supabase to process on reset-password page
  window.location.href = '/reset-password' + window.location.hash;
}

// ─── Durable session storage ────────────────────────────────────────
//
// iOS Safari aggressively evicts localStorage AND IndexedDB from standalone
// (Home Screen) PWAs under ITP, which logs the user out every time they
// reopen the app. We pick a durable backing store per surface:
//
//   • Capacitor native → @capacitor/preferences (keychain / SharedPreferences,
//                       survives app kill).
//   • Web (incl. PWA)  → triple-write to a long-lived cookie, localStorage,
//                       and IndexedDB. Cookies survive iOS PWA storage
//                       eviction much better than LS/IDB; LS gives us the
//                       fastest hot path; IDB is a third backup. Reads check
//                       in order LS → cookie → IDB and rehydrate the others
//                       from whichever survived.
//
// All branches expose the synchronous-or-async storage shape that
// supabase-js awaits.

interface SupabaseStorage {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

const IDB_DB_NAME = "noknok-auth";
const IDB_STORE_NAME = "session";

function openAuthIdb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_DB_NAME, 1);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(IDB_STORE_NAME)) {
          db.createObjectStore(IDB_STORE_NAME);
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function idbGet(key: string): Promise<string | null> {
  const db = await openAuthIdb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(IDB_STORE_NAME, "readonly");
      const req = tx.objectStore(IDB_STORE_NAME).get(key);
      req.onsuccess = () => resolve(typeof req.result === "string" ? req.result : null);
      req.onerror = () => resolve(null);
      tx.oncomplete = () => db.close();
    } catch {
      try { db.close(); } catch { /* ignore */ }
      resolve(null);
    }
  });
}

function idbSet(key: string, value: string): void {
  // Fire-and-forget — never block the auth path.
  void openAuthIdb().then((db) => {
    if (!db) return;
    try {
      const tx = db.transaction(IDB_STORE_NAME, "readwrite");
      tx.objectStore(IDB_STORE_NAME).put(value, key);
      tx.oncomplete = () => db.close();
      tx.onerror = () => { try { db.close(); } catch { /* ignore */ } };
    } catch {
      try { db.close(); } catch { /* ignore */ }
    }
  });
}

function idbDelete(key: string): void {
  void openAuthIdb().then((db) => {
    if (!db) return;
    try {
      const tx = db.transaction(IDB_STORE_NAME, "readwrite");
      tx.objectStore(IDB_STORE_NAME).delete(key);
      tx.oncomplete = () => db.close();
      tx.onerror = () => { try { db.close(); } catch { /* ignore */ } };
    } catch {
      try { db.close(); } catch { /* ignore */ }
    }
  });
}

// ─── Cookie storage (iOS PWA durable layer) ─────────────────────────
//
// One value can exceed the per-cookie ~4KB limit, so we chunk: when a
// value is larger than COOKIE_CHUNK_SIZE we write it across `<key>.0`,
// `<key>.1`, ... and a `<key>.count` index cookie. Otherwise the value
// lives in a single cookie at `<key>`.
//
// Cookies use Max-Age=1y, SameSite=Lax, Secure, Path=/ so they're sent
// to our app on every navigation. They are NOT sent to supabase.co
// (different domain), so they don't bloat Supabase API requests.

const COOKIE_CHUNK_SIZE = 3500;
const COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 365;

function readRawCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${escaped}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeRawCookie(name: string, value: string): void {
  if (typeof document === "undefined") return;
  const isSecure = window.location.protocol === "https:";
  const secureAttr = isSecure ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${COOKIE_MAX_AGE_SEC}; Path=/; SameSite=Lax${secureAttr}`;
}

function deleteRawCookie(name: string): void {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
}

function cookieGet(key: string): string | null {
  const single = readRawCookie(key);
  if (single !== null) return single;
  const countRaw = readRawCookie(`${key}.count`);
  if (!countRaw) return null;
  const count = parseInt(countRaw, 10);
  if (!Number.isFinite(count) || count <= 0) return null;
  let combined = "";
  for (let i = 0; i < count; i += 1) {
    const part = readRawCookie(`${key}.${i}`);
    if (part === null) return null;
    combined += part;
  }
  return combined;
}

function cookieDeleteAll(key: string): void {
  deleteRawCookie(key);
  const countRaw = readRawCookie(`${key}.count`);
  if (countRaw) {
    const count = parseInt(countRaw, 10);
    if (Number.isFinite(count) && count > 0) {
      for (let i = 0; i < count; i += 1) deleteRawCookie(`${key}.${i}`);
    }
    deleteRawCookie(`${key}.count`);
  }
}

function cookieSet(key: string, value: string): void {
  cookieDeleteAll(key);
  if (value.length <= COOKIE_CHUNK_SIZE) {
    writeRawCookie(key, value);
    return;
  }
  const chunks = Math.ceil(value.length / COOKIE_CHUNK_SIZE);
  writeRawCookie(`${key}.count`, String(chunks));
  for (let i = 0; i < chunks; i += 1) {
    writeRawCookie(`${key}.${i}`, value.slice(i * COOKIE_CHUNK_SIZE, (i + 1) * COOKIE_CHUNK_SIZE));
  }
}

function buildWebStorage(): SupabaseStorage {
  return {
    async getItem(key: string) {
      // 1. Hot path — synchronous localStorage hit.
      try {
        const fromLs = window.localStorage.getItem(key);
        if (fromLs !== null) return fromLs;
      } catch { /* localStorage unavailable */ }

      // 2. Cookie fallback — durable on iOS PWA where ITP wipes LS.
      const fromCookie = cookieGet(key);
      if (fromCookie !== null) {
        try { window.localStorage.setItem(key, fromCookie); } catch { /* ignore */ }
        idbSet(key, fromCookie);
        return fromCookie;
      }

      // 3. IndexedDB fallback — covers any case where cookies were cleared
      //    but IDB survived (or vice-versa during deploys / upgrades).
      const fromIdb = await idbGet(key);
      if (fromIdb !== null) {
        try { window.localStorage.setItem(key, fromIdb); } catch { /* ignore */ }
        cookieSet(key, fromIdb);
      }
      return fromIdb;
    },
    setItem(key: string, value: string) {
      try { window.localStorage.setItem(key, value); } catch { /* ignore */ }
      cookieSet(key, value);
      idbSet(key, value);
    },
    removeItem(key: string) {
      try { window.localStorage.removeItem(key); } catch { /* ignore */ }
      cookieDeleteAll(key);
      idbDelete(key);
    },
  };
}

// Ephemeral per-document store for the read-only marketing embed. Never
// touches localStorage / cookies / IndexedDB, so it can't clobber a real
// user's session on the shared noknok.pro origin.
function buildMemoryStorage(): SupabaseStorage {
  const mem = new Map<string, string>();
  return {
    getItem: (key) => (mem.has(key) ? mem.get(key)! : null),
    setItem: (key, value) => { mem.set(key, value); },
    removeItem: (key) => { mem.delete(key); },
  };
}

function buildNativeStorage(): SupabaseStorage {
  // Lazy dynamic import so the web bundle never pulls in @capacitor/preferences.
  const prefs = import("@capacitor/preferences").then((m) => m.Preferences);
  return {
    async getItem(key: string) {
      const Preferences = await prefs;
      const { value } = await Preferences.get({ key });
      return value ?? null;
    },
    async setItem(key: string, value: string) {
      const Preferences = await prefs;
      await Preferences.set({ key, value });
    },
    async removeItem(key: string) {
      const Preferences = await prefs;
      await Preferences.remove({ key });
    },
  };
}

const authStorage: SupabaseStorage = IS_NATIVE
  ? buildNativeStorage()
  : IS_DEMO_EMBED
    ? buildMemoryStorage()
    : buildWebStorage();

const embedProjectRef = (() => {
  try { return new URL(supabaseUrl).hostname.split(".")[0]; } catch { return "noknok"; }
})();

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: authStorage,
    // Distinct key + in-memory store so the embed never collides with the real
    // app's `sb-<ref>-auth-token`. Also don't parse the URL for a session in
    // the embed — it fetches its scoped viewer token from the demo-session fn.
    ...(IS_DEMO_EMBED ? { storageKey: `sb-${embedProjectRef}-demo-embed` } : {}),
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: !IS_DEMO_EMBED,
  },
});

// ─── Durable-session peek ───────────────────────────────────────────
//
// supabase-js stores the whole session under one key (`sb-<ref>-auth-token`).
// On a just-resumed iOS PWA, getSession() can momentarily return null while
// the token refresh is still waiting on a network round-trip that isn't ready
// yet — which previously bounced the user to /auth even though a perfectly
// valid session was sitting in our durable (cookie/IDB/LS) storage. The auth
// gate uses this to tell "genuinely logged out" (nothing persisted) apart from
// "logged in, just hasn't rehydrated yet" (token present) so it never ejects a
// real session over a transient hiccup.
const projectRef = (() => {
  try { return new URL(supabaseUrl).hostname.split(".")[0]; } catch { return ""; }
})();
export const AUTH_STORAGE_KEY = `sb-${projectRef}-auth-token`;

export async function hasDurableStoredSession(): Promise<boolean> {
  try {
    const raw = await authStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return false;
    // A present-but-empty/garbage value shouldn't count as a session.
    return raw.length > 2;
  } catch {
    return false;
  }
}

// Also set up listener as fallback for any edge cases
supabase.auth.onAuthStateChange((event) => {
  if (event === 'PASSWORD_RECOVERY') {
    // Redirect to reset password page if not already there
    if (window.location.pathname !== '/reset-password') {
      window.location.href = '/reset-password';
    }
  }
});
